import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { DialogShellBody, DialogShellFooter, DialogShellHeader } from '@bliks-io/ui';
import type {
  AddSystemRequest,
  AddOrgFunctionRequest,
  PopulatedEntitySuggestion,
  PopulatedSystemEntitySuggestion,
  PopulatedOrgFunctionEntitySuggestion,
  System,
  UpdateSystemRequest,
  UpdateOrgFunctionRequest
} from '@bliks-io/contracts';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import { useApproveEntitySuggestion } from '@/api/mutations/useApproveEntitySuggestion';
import { useMergeEntitySuggestion } from '@/api/mutations/useMergeEntitySuggestion';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { useDuplicateCandidates } from '@/api/queries/useDuplicateCandidates';
import { SystemFormDialog } from '@/pages/organization/systems/components/SystemFormDialog';
import { OrgFunctionFormDialog } from '@/pages/organization/org-functions/components/OrgFunctionFormDialog';
import { RecordingContextBlock } from './RecordingContextBlock';
import { ProposedEntityDetails } from './ProposedEntityDetails';
import { ExistingEntityMergePanel, type MergeTarget } from './ExistingEntityMergePanel';
import { MergeConfirmDialog } from './MergeConfirmDialog';
import { buildDuplicateCandidatesRequest } from './duplicate-candidates';

interface SuggestionReviewBodyProps {
  suggestion: PopulatedEntitySuggestion;
  onClose: () => void;
  onReject: () => void;
  onPreviewRecording: () => void;
}

/**
 * Review copy is system / team / function specific — orgFunction suggestions
 * branch on the proposed `payload.kind`, since the UI never shows an umbrella
 * "OrgFunction" label (ADR 0004).
 */
function reviewCopyKeys(suggestion: PopulatedEntitySuggestion): {
  title: string;
  description: string;
  createAsNew: string;
} {
  if (suggestion.kind === 'system') {
    return {
      title: 'entitySuggestion.review.titleSystem',
      description: 'entitySuggestion.review.descriptionSystem',
      createAsNew: 'entitySuggestion.review.createAsNew.system'
    };
  }
  if (suggestion.payload.kind === 'team') {
    return {
      title: 'entitySuggestion.review.titleTeamSuggestion',
      description: 'entitySuggestion.review.descriptionTeamSuggestion',
      createAsNew: 'entitySuggestion.review.createAsNew.team'
    };
  }
  return {
    title: 'entitySuggestion.review.titleFunctionSuggestion',
    description: 'entitySuggestion.review.descriptionFunctionSuggestion',
    createAsNew: 'entitySuggestion.review.createAsNew.function'
  };
}

/**
 * Step 1 of the accept flow: a light decision surface. It shows the recording
 * context, offers a "merge into an existing entity" path, and — via "Create as
 * new …" — opens the real create modal (nested on top, prefilled from the
 * suggestion) whose submit is routed to the approve mutation. Reusing the real
 * create dialog keeps the create fields/validation/copy in one place instead of
 * a duplicated review form.
 */
export function SuggestionReviewBody({
  suggestion,
  onClose,
  onReject,
  onPreviewRecording
}: SuggestionReviewBodyProps) {
  const { t } = useTranslation();
  const merge = useMergeEntitySuggestion(suggestion._id);
  const [pendingTarget, setPendingTarget] = useState<MergeTarget | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const candidatesBody = useMemo(() => buildDuplicateCandidatesRequest(suggestion), [suggestion]);
  const { data: candidates = [], isLoading: isLoadingCandidates } = useDuplicateCandidates(
    candidatesBody,
    { excludeSuggestionId: suggestion._id }
  );

  const handleConfirmMerge = async () => {
    if (!pendingTarget) return;
    try {
      await merge.mutateAsync({ targetEntityId: pendingTarget.id });
      toast.success(t('entitySuggestion.review.merged'));
      onClose();
    } catch (err) {
      console.error('Failed to merge suggestion:', err);
      toastApiError(err, 'common.errorOccurred');
    }
  };

  const handleApproved = () => {
    toast.success(t('entitySuggestion.review.approved'));
    onClose();
  };

  const isMerging = merge.isPending;
  const isBusy = isMerging || pendingTarget !== null;
  const Icon = ENTITY_ICONS[suggestion.kind];
  const copyKeys = reviewCopyKeys(suggestion);

  return (
    <>
      <div className="flex flex-1 min-h-0 flex-col">
        <DialogShellHeader
          icon={<Icon className="h-5 w-5" />}
          title={t(copyKeys.title)}
          subtitle={t(copyKeys.description, { name: suggestion.payload.name })}
          onClose={onClose}
        />
        <DialogShellBody>
          <RecordingContextBlock suggestion={suggestion} onPreviewClick={onPreviewRecording} />
          <ProposedEntityDetails suggestion={suggestion} />
          <ExistingEntityMergePanel
            kind={suggestion.kind}
            candidates={candidates}
            isLoadingCandidates={isLoadingCandidates}
            selectValue={pendingTarget?.id}
            onPickTarget={setPendingTarget}
            disabled={isBusy}
          />
        </DialogShellBody>
        <DialogShellFooter
          danger={{
            label: t('entitySuggestion.review.reject'),
            onClick: onReject,
            disabled: isBusy
          }}
          secondary={{
            label: t('common.cancel'),
            onClick: onClose,
            disabled: isBusy
          }}
          primary={{
            label: t(copyKeys.createAsNew),
            onClick: () => setShowCreate(true),
            disabled: isBusy
          }}
        />
      </div>

      <MergeConfirmDialog
        open={pendingTarget !== null}
        kind={suggestion.kind}
        targetName={pendingTarget?.name ?? ''}
        suggestionName={suggestion.payload.name}
        isMerging={isMerging}
        onCancel={() => setPendingTarget(null)}
        onConfirm={handleConfirmMerge}
      />

      {suggestion.kind === 'system' ? (
        <SystemApproveCreateDialog
          suggestion={suggestion}
          open={showCreate}
          onOpenChange={setShowCreate}
          onApproved={handleApproved}
        />
      ) : (
        <OrgFunctionApproveCreateDialog
          suggestion={suggestion}
          open={showCreate}
          onOpenChange={setShowCreate}
          onApproved={handleApproved}
        />
      )}
    </>
  );
}

interface ApproveCreateDialogProps<TSuggestion> {
  suggestion: TSuggestion;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Close the whole review modal + toast once the entity is actually created. */
  onApproved: () => void;
}

function SystemApproveCreateDialog({
  suggestion,
  open,
  onOpenChange,
  onApproved
}: ApproveCreateDialogProps<PopulatedSystemEntitySuggestion>) {
  const { t } = useTranslation();
  const approve = useApproveEntitySuggestion(suggestion._id);

  // Returns the new system's id so the dialog can chain its logo upload; a
  // failure propagates to FormDialog, which surfaces it and leaves the dialog
  // open. The dialog fires `onSuccess` only when an entity comes back.
  const handleApprove = async (
    data: AddSystemRequest | UpdateSystemRequest
  ): Promise<Pick<System, '_id'> | undefined> => {
    const p = data as AddSystemRequest; // never edit-mode in the approve flow
    const res = await approve.mutateAsync({
      kind: 'system',
      payload: {
        name: p.name,
        description: p.description || undefined,
        category: p.category || undefined,
        url: p.url || undefined,
        systemOwner: p.systemOwner,
        licenceSeats: p.licenceSeats
      }
    });
    const id = res.data.resolvedEntityId;
    return id ? { _id: id } : undefined;
  };

  return (
    <SystemFormDialog
      open={open}
      onOpenChange={onOpenChange}
      defaultValuesOverride={{
        name: suggestion.payload.name,
        description: suggestion.payload.description,
        category: suggestion.payload.category,
        url: suggestion.payload.url,
        systemOwner: suggestion.payload.systemOwner,
        licenceSeats: suggestion.payload.licenceSeats
      }}
      title={t('entitySuggestion.review.titleApproveSystem')}
      description={t('entitySuggestion.review.descriptionApproveSystem', {
        name: suggestion.payload.name
      })}
      submitLabel={t('entitySuggestion.review.approveAndCreate')}
      onSubmit={handleApprove}
      onSuccess={onApproved}
    />
  );
}

function OrgFunctionApproveCreateDialog({
  suggestion,
  open,
  onOpenChange,
  onApproved
}: ApproveCreateDialogProps<PopulatedOrgFunctionEntitySuggestion>) {
  const { t } = useTranslation();
  const approve = useApproveEntitySuggestion(suggestion._id);
  const isTeam = suggestion.payload.kind === 'team';

  // No logo step on OrgFunctions, so close + toast straight from the adapter on
  // success; errors surface in the dialog's alert and leave it open. `members`
  // are seeded from the suggestion into the create form's people picker, so the
  // reviewer's final selection is what approval fans into each user's
  // secondaryRoles. The created kind is the suggestion's kind, taken from the
  // payload (not the form — the form has no kind field): kind is fixed at
  // suggestion time, a misclassified suggestion is rejected and re-suggested.
  const handleApprove = async (data: AddOrgFunctionRequest | UpdateOrgFunctionRequest) => {
    const p = data as AddOrgFunctionRequest; // never edit-mode in the approve flow
    await approve.mutateAsync({
      kind: 'orgFunction',
      payload: {
        name: p.name,
        kind: suggestion.payload.kind,
        description: p.description || undefined,
        department: p.department || undefined,
        lead: p.lead || undefined,
        email: p.email || undefined,
        members: p.members
      }
    });
    onApproved();
  };

  return (
    <OrgFunctionFormDialog
      open={open}
      onOpenChange={onOpenChange}
      kind={suggestion.payload.kind}
      defaultValuesOverride={{
        name: suggestion.payload.name,
        description: suggestion.payload.description,
        email: suggestion.payload.email,
        lead: suggestion.payload.lead,
        department: suggestion.payload.department?._id,
        members: suggestion.payload.members
      }}
      title={t(
        isTeam
          ? 'entitySuggestion.review.titleApproveTeam'
          : 'entitySuggestion.review.titleApproveFunction'
      )}
      description={t(
        isTeam
          ? 'entitySuggestion.review.descriptionApproveTeam'
          : 'entitySuggestion.review.descriptionApproveFunction',
        { name: suggestion.payload.name }
      )}
      submitLabel={t('entitySuggestion.review.approveAndCreate')}
      onSubmit={handleApprove}
    />
  );
}
